import { useEffect, useState, type ReactElement } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next/pages'
import { ConfirmBox } from '@corratech/pylot-ui'
import { useAddToCart, useCart } from '@corratech/pylot-cart-manager'
import { useRemoveItemFromCart } from '@corratech/pylot-cart-manager/src/use-remove-item-from-cart'
import {
    closeGiftCardMixAlert,
    hideGiftCardMixAlert,
    subscribeGiftCardMixAlert,
    takePendingGiftCardMixAdd
} from '@corratech/pylot-cart-manager/src/utils/giftCardMixAlert'
import { isGiftCardLikeCartItem } from '@corratech/pylot-cart-manager/src/utils/giftCardCartMix'
import { useUser, useAuthUI } from '@corratech/pylot-auth-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import {
    useAddProductsToWishlist,
    useCreateWishlist,
    useWishlist
} from '@corratech/pylot-wishlist'
import { WishlistVisibilityEnum } from '@pylot-data/enums/WishlistVisibilityEnum'
import type { ConfigurableCartItem } from '@pylot-data/pylotschema'

const SAVE_FOR_LATER_WISHLIST_NAME = 'GUARDAR PARA DESPUES'

export const CartMixAlert = (): ReactElement => {
    const { t } = useTranslation('common')
    const { addToCart } = useAddToCart()
    const { data: cartData } = useCart()
    const { removeAllItemsFromCart, removeItemFromCart } =
        useRemoveItemFromCart()
    const { isSignedIn } = useUser()
    const { openAuthModal, onLoginCallback, onSignUpCallback } = useAuthUI()
    const {
        openCartToast,
        openToast,
        closeSidebar,
        closeCartToast,
        closeToast,
        displaySidebar
    } = useUI()
    const { wishlists } = useWishlist()
    const { createWishList } = useCreateWishlist()
    const { addProductsToWishlist } = useAddProductsToWishlist()
    const [show, setShow] = useState(false)
    const [isConfirming, setIsConfirming] = useState(false)

    useEffect(() => subscribeGiftCardMixAlert(setShow), [])

    const handleClose = (): void => {
        if (isConfirming) {
            return
        }
        closeGiftCardMixAlert()
    }

    const retryPendingGiftAdd = async (): Promise<void> => {
        const pending = takePendingGiftCardMixAdd()
        if (!pending) return
        await addToCart(
            pending.cartItems,
            pending.products,
            pending.excludeItems,
            false,
            pending.displayLoadingIndicator
        )
    }

    const handleEmptyCart = async (): Promise<void> => {
        setIsConfirming(true)
        try {
            await removeAllItemsFromCart()
            await retryPendingGiftAdd()
        } catch {
            // Pending queda para reintentar si Magento aún no vació.
        } finally {
            setIsConfirming(false)
        }
    }

    const handleSaveProducts = async (): Promise<void> => {
        if (isConfirming) return

        if (!isSignedIn) {
            hideGiftCardMixAlert()
            closeSidebar()
            openAuthModal()
            onLoginCallback([handleSaveProducts])
            onSignUpCallback([handleSaveProducts])
            return
        }

        setIsConfirming(true)
        try {
            const items = (cartData?.data?.cart?.items ?? []).filter(
                (item): item is NonNullable<typeof item> =>
                    Boolean(item) && !isGiftCardLikeCartItem(item)
            )

            if (!items.length) {
                await retryPendingGiftAdd()
                return
            }

            const matchedWishlist = (wishlists || []).find(
                (wishlist) =>
                    wishlist?.name?.trim().toUpperCase() ===
                    SAVE_FOR_LATER_WISHLIST_NAME
            )
            let targetWishlistId = matchedWishlist?.id

            if (!targetWishlistId) {
                const createResponse = await createWishList({
                    name: SAVE_FOR_LATER_WISHLIST_NAME,
                    visibility: WishlistVisibilityEnum.Private
                })
                targetWishlistId =
                    createResponse?.data?.createWishlist?.wishlist?.id
            }

            if (!targetWishlistId) {
                throw new Error('Wishlist not found')
            }

            for (const item of items) {
                const productSku = item.product?.sku
                if (!productSku || !item.uid) continue

                const configurableItem = item as ConfigurableCartItem
                const selected_options = Array.isArray(
                    configurableItem.configurable_options
                )
                    ? (configurableItem.configurable_options
                          .map(
                              (option) =>
                                  option?.configurable_product_option_value_uid
                          )
                          .filter(Boolean) as string[])
                    : undefined

                const addResponse = await addProductsToWishlist({
                    wishlistId: Number(targetWishlistId),
                    itemOptions: [
                        {
                            sku: productSku,
                            quantity: item.quantity,
                            selected_options
                        }
                    ]
                })

                if (
                    addResponse?.errors?.length ||
                    !addResponse?.data?.addProductsToWishlist?.wishlist
                ) {
                    throw new Error('Add to wishlist failed')
                }

                await removeItemFromCart(item.uid, false)
            }

            const successMessage = (
                <span className="inline-flex flex-wrap items-baseline gap-x-1">
                    <span>{t('cart|Saved for later in favorites list')}</span>
                    <Link
                        href="/account/wishlist"
                        className="underline"
                        onClick={() => {
                            closeCartToast()
                            closeToast()
                            closeSidebar()
                        }}
                    >
                        {t('cart|View wishlist here')}
                    </Link>
                </span>
            )
            if (displaySidebar) {
                openCartToast(successMessage, ToastType.Success, true)
            } else {
                openToast(successMessage, ToastType.Success, true)
            }

            await retryPendingGiftAdd()
        } catch {
            if (displaySidebar) {
                openCartToast(
                    t('cart|Could not save for later'),
                    ToastType.Danger,
                    true
                )
            } else {
                openToast(
                    t('cart|Could not save for later'),
                    ToastType.Danger,
                    true
                )
            }
        } finally {
            setIsConfirming(false)
        }
    }

    return (
        <ConfirmBox
            show={show}
            closeConfirm={handleClose}
            handleAction={handleSaveProducts}
            handleTertiaryAction={handleEmptyCart}
            icon="warning"
            title={t('cart|Gift card mix title')}
            description={t('cart|Gift card mix confirmation')}
            confirmString={t('cart|Gift card mix save products')}
            tertiaryString={t('cart|Gift card mix empty cart')}
            confirmLoading={isConfirming}
            variant="cart"
        />
    )
}
